import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../../../environments/environment';
import { NotificationService } from './notification.service';
import { first, Observable, of } from 'rxjs';
import { UserService } from '../../user/services/user.service';
import { ReturnUrlService } from '../../auth/services/return-url.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private dialogRef = inject(MatDialog);
  private notificationService = inject(NotificationService);
  private zone = inject(NgZone);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private returnUrlService = inject(ReturnUrlService);
  private userService!: UserService;
  private injector = inject(Injector);

  public handleError(error: HttpErrorResponse): Promise<any> {
    if (!environment.production) {
      console.error(error);
    }

    let message = this.extractErrorMessage(error);
    switch (error.status) {
      case 0:
        message = this.translateService.instant('messages.error.misc.unauthenticated');
        // For network errors, don't redirect - they might be temporary
        break;

      case 401:
        // Let the token interceptor handle 401 errors
        // Don't redirect here as it will conflict with the interceptor
        message = this.translateService.instant('messages.error.misc.unauthenticated');
        break;

      case 500:
        message = this.translateService.instant('messages.error.generic');
        this.snackBarOpen(message);
        break;

      case 422:
        break;

      default:
        //message = this.translateService.instant('messages.error.generic');
        //this.snackBarOpen(message);
        break;
    }
    console.log(message);
    return Promise.reject(error);
  }

  private isInAuthContext(): boolean {
    const currentUrl = this.router.url;
    // Don't redirect if we're already on auth pages or OAuth2 callback
    return currentUrl.includes('/auth/') ||
      currentUrl.includes('/oauth2/callback') ||
      currentUrl === '/' ||
      currentUrl.includes('/login');
  }

  public handleValidationError(err: any): Observable<any> {
    if (err.status === 422) {
      return of(err.error.errors);
    }
    return of(null);
  }

  private snackBarOpen(message: string): void {
    this.zone.run(() => {
      this.notificationService.error(message);
    });
  }

  private logoutUser(): void {
    if (!this.userService) {
      this.userService = this.injector.get(UserService);
    }
    this.userService.logout().pipe(first()).subscribe(() => this.router.navigate(['login'])).unsubscribe();
  }

  private extractErrorMessage(error: any): string {
    const message = error?.error?.message || error?.error?.error || error?.message
      || 'messages.error.misc.generic';
    return this.translateService.instant(message);
  }
}
